import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import {
  deleteTeamsReportRequest,
  getTeamsReportsRequest,
  TeamsReportRequest,
} from '../../../api/reports/teamsReports';
import { NotificationContext } from '../../../contexts/notificationContext';
import { POLL_INTERVAL_MS, REPORTS_PAGE_SIZE } from '../constants';
import { getReportErrorMessage, isReportInProgress } from '../utils';

/**
 * The last 30 days of reports, newest first. While any loaded row is queued or generating,
 * the loaded pages are re-read every 5 s so those rows move on by themselves.
 */
export const useReportsHistory = (companyId?: string) => {
  const { showNotification } = useContext(NotificationContext);
  const [items, setItems] = useState<TeamsReportRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const loadId = useRef(0);

  const load = useCallback(
    async (pageCount: number, quiet = false) => {
      if (!companyId) return;
      const id = ++loadId.current;
      if (!quiet) setLoading(true);
      try {
        // One request for everything shown, so a refresh never reorders or drops rows
        const { data } = await getTeamsReportsRequest(companyId, {
          page: 1,
          page_size: Math.min(REPORTS_PAGE_SIZE * pageCount, 100),
        });
        if (id !== loadId.current) return;
        setItems(data.items);
        setTotal(data.total);
        setLoadError(null);
      } catch (e) {
        if (id !== loadId.current) return;
        if (!quiet) setLoadError(getReportErrorMessage(e).title);
      } finally {
        if (id === loadId.current) setLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    setPages(1);
    setItems([]);
    load(1);
  }, [load]);

  const hasActive = items.some(item => isReportInProgress(item.status));
  useEffect(() => {
    if (!hasActive) return;
    const timer = setInterval(() => load(pages, true), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [hasActive, load, pages]);

  const showMore = useCallback(() => {
    const next = pages + 1;
    setPages(next);
    load(next, true);
  }, [pages, load]);

  const refresh = useCallback(() => load(pages, true), [load, pages]);

  const remove = useCallback(
    async (requestId: string) => {
      if (!companyId) return;
      setDeletingId(requestId);
      try {
        await deleteTeamsReportRequest(companyId, requestId);
        setItems(prev => prev.filter(item => item.request_id !== requestId));
        setTotal(prev => Math.max(prev - 1, 0));
        showNotification({ message: 'Report removed from your history', options: { variant: 'success' } });
      } catch (e) {
        const { title, description } = getReportErrorMessage(e);
        showNotification({ message: title, description, options: { variant: 'error' } });
      } finally {
        setDeletingId(null);
      }
    },
    [companyId, showNotification]
  );

  return {
    items,
    total,
    loading,
    loadError,
    canShowMore: items.length < total && items.length < 100,
    showMore,
    refresh,
    remove,
    deletingId,
  };
};
